<template>
  <v-container>
    <v-row justify="center" align="center" style="height: 100vh">
      <v-col cols="12" sm="8" md="4">
        <v-card>
          <v-card-title class="text-h5 d-flex text-center justify-center"
            >IS Global <br />
            Applications and Tools</v-card-title
          >
          <v-card-text>
            <!-- Teams Context Info -->
            <v-alert v-if="isInTeams" type="info" variant="tonal" class="mb-4">
              <v-icon left>mdi-microsoft-teams</v-icon>
              Running in Microsoft Teams
            </v-alert>

            <!-- Microsoft Login Button -->
            <v-btn
              color="#0078d4"
              variant="flat"
              block
              size="large"
              class="mb-4"
              :loading="isLoading"
              @click="loginWithMicrosoft"
            >
              <v-icon start>mdi-microsoft</v-icon>
              {{ isInTeams ? 'Sign in with Teams' : 'Sign in with Microsoft' }}
            </v-btn>

            <!-- Show traditional login only if not in Teams -->
            <template v-if="!isInTeams">
              <!-- Divider -->
              <v-divider class="my-4">
                <span class="text-caption text-medium-emphasis px-2">OR</span>
              </v-divider>

              <!-- Traditional Login Form -->
              <v-form ref="form" v-model="valid" lazy-validation @keydown.enter="login">
                <v-text-field
                  v-model="username"
                  :rules="usernameRules"
                  label="Username"
                  required
                  variant="underlined"
                ></v-text-field>
                <v-text-field
                  v-model="password"
                  :rules="passwordRules"
                  label="Password"
                  type="password"
                  required
                  variant="underlined"
                ></v-text-field>
              </v-form>
            </template>
            <v-alert
              v-if="error || authError"
              border="start"
              closable
              :type="authError && authError.includes('Redirect initiated') ? 'info' : 'error'"
              variant="tonal"
              @click:close="closeAlert"
              >{{ error || authError }}</v-alert
            >
          </v-card-text>
          <v-card-actions v-if="!isInTeams">
            <v-btn color="#3287CB" variant="flat" block @click="login">Login</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { encryptString } from '@/helper/helper'
import { useAuth } from '@/composables/useAuth'

export default {
  setup() {
    const router = useRouter()
    const username = ref('')
    const password = ref('')
    const valid = ref(false)

    // Use the auth composable for Microsoft login
    const { isLoading, error: authError, loginWithMicrosoft, clearError, isInTeams } = useAuth()

    // Combine auth error with form error
    const error = ref('')

    const usernameRules = [
      (v: string) => !!v || 'Username is required',
      (v: string) => (v && v.length >= 3) || 'Username must be at least 3 characters',
    ]

    const passwordRules = [
      (v: string) => !!v || 'Password is required',
      (v: string) => (v && v.length >= 6) || 'Password must be at least 6 characters',
    ]

    const login = async () => {
      if (valid.value) {
        const hashPassword = encryptString(password.value, import.meta.env.VITE_SALT)
        // Simulate an API call for authentication
        const response = await fakeApiLogin(username.value, hashPassword)
        if (response.success) {
          // console.log(response)
          // Store authentication state in localStorage or sessionStorage
          localStorage.setItem('token', response.token)
          // Redirect to home or another page
          router.push({ name: 'Home' }) // Adjust the route name as needed
        } else {
          error.value = response.message // Show error message
        }
      }
    }

    // Simulated API login function
    const fakeApiLogin = async (username: string, password: string) => {
      try {
        const response = await axios.post('api/login', {
          // const response = await axios.post('http://localhost:3081/login', {
          username: username,
          password: password,
        })

        return {
          success: true,
          message: 'Login successful',
          token: response.data.token,
        }
      } catch {
        // console.log(err)
        return {
          success: false,
          message: 'Invalid credentials.',
        }
      }
    }

    const closeAlert = () => {
      error.value = ''
      clearError()
    }

    // Watch for auth errors and update local error
    const updateError = () => {
      error.value = authError.value || ''
    }

    return {
      username,
      password,
      valid,
      error,
      authError,
      isLoading,
      isInTeams,
      usernameRules,
      passwordRules,
      login,
      loginWithMicrosoft,
      closeAlert,
      updateError,
    }
  },
}
</script>

<style scoped>
/* Add any styles you need here */
</style>
